import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Oystehr from '@oystehr/sdk';
import { Location, Schedule, Slot } from 'fhir/r4b';
import { DateTime } from 'luxon';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AddVisitPatientInformationCard } from 'src/features/visits/shared/components/staff-add-visit/AddVisitPatientInformationCard';
import {
  BOOKING_CONFIG,
  CreateAppointmentInputParams,
  CreateSlotParams,
  getAppointmentDurationFromSlot,
  getReasonForVisitOptionsForServiceCategory,
  GetScheduleRequestParams,
  GetScheduleResponse,
  getTimezone,
  PatientInfo,
  ScheduleType,
  ServiceMode,
  SLUG_SYSTEM,
} from 'utils';
import { createAppointment, createSlot, getLocations } from '../api/api';
import CustomBreadcrumbs from '../components/CustomBreadcrumbs';
import { CustomDialog } from '../components/dialogs/CustomDialog';
import LocationSelect, { LocationType } from '../components/LocationSelect';
import SlotPicker from '../components/SlotPicker';
import { MAXIMUM_CHARACTER_LIMIT } from '../constants';
import { dataTestIds } from '../constants/data-test-ids';
import { useApiClients } from '../hooks/useAppClients';
import PageContainer from '../layout/PageContainer';

type SlotLoadingState =
  | { status: 'initial'; input: undefined }
  | { status: 'loading'; input: undefined }
  | { status: 'loaded'; input: string };

export type AddVisitFormState =
  | 'existingPatientSelected'
  | 'manuallyEnterPatientDetails'
  | 'initialPatientSearch'
  | 'displayPatientSearch';

export interface AddVisitErrorState {
  submit?: boolean;
  visitType?: boolean;
  serviceCategory?: boolean;
  location?: boolean;
  firstName?: boolean;
  lastName?: boolean;
  phone?: boolean;
  dateOfBirth?: boolean;
  sexAtBirth?: boolean;
  reasonForVisit?: boolean;
  search?: boolean;
  searchEntry?: boolean;
}

export type AddVisitPatientInfo = Pick<
  PatientInfo,
  'id' | 'newPatient' | 'firstName' | 'middleName' | 'lastName' | 'dateOfBirth' | 'sex' | 'phoneNumber'
>;
export interface LocationWithWalkinSchedule extends Location {
  walkinSchedule: Schedule | undefined;
}

const defaultServiceCategory =
  BOOKING_CONFIG.serviceCategories.length === 1 ? BOOKING_CONFIG.serviceCategories[0]?.category.code : '';

// todo: this lives in the util folder and is redundantly declared here - should be consolidated
enum VisitType {
  InPersonWalkIn = 'in-person-walk-in',
  InPersonPreBook = 'in-person-pre-booked',
  InPersonPostTelemed = 'in-person-post-telemed',
  VirtualOnDemand = 'virtual-on-demand',
  VirtualScheduled = 'virtual-scheduled',
}

export default function AddPatient(): JSX.Element {
  const location = useLocation();
  const followUpState = location.state as
    | {
        parentEncounterId?: string;
        parentLocation?: LocationWithWalkinSchedule;
        patientId?: string;
        patientInfo?: AddVisitPatientInfo;
      }
    | undefined;
  const parentEncounterId = followUpState?.parentEncounterId;
  const isScheduledFollowUp = !!parentEncounterId;
  console.log('[AddPatient] location.state:', location.state, 'parentEncounterId:', parentEncounterId);

  const [selectedLocation, setSelectedLocation] = useState<LocationWithWalkinSchedule | undefined>(
    followUpState?.parentLocation
  );
  const [birthDate, setBirthDate] = useState<DateTime | null>(
    followUpState?.patientInfo?.dateOfBirth ? DateTime.fromISO(followUpState.patientInfo.dateOfBirth) : null
  );
  const [patientInfo, setPatientInfo] = useState<AddVisitPatientInfo | undefined>(
    followUpState?.patientInfo || undefined
  );
  const [reasonForVisit, setReasonForVisit] = useState<string>('');
  const [reasonForVisitAdditional, setReasonForVisitAdditional] = useState<string>('');
  const [visitType, setVisitType] = useState<VisitType>();
  const [serviceCategory, setServiceCategory] = useState<string>(defaultServiceCategory);
  const [slot, setSlot] = useState<Slot | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<AddVisitErrorState>({
    submit: false,
    visitType: false,
    serviceCategory: false,
    location: false,
    firstName: false,
    lastName: false,
    phone: false,
    dateOfBirth: false,
    sexAtBirth: false,
    reasonForVisit: false,
    search: false,
    searchEntry: false,
  });
  const [loadingSlotState, setLoadingSlotState] = useState<SlotLoadingState>({ status: 'initial', input: undefined });
  const [locationWithSlotData, setLocationWithSlotData] = useState<GetScheduleResponse | undefined>(undefined);
  const [validDate, setValidDate] = useState<boolean>(true);
  const [selectSlotDialogOpen, setSelectSlotDialogOpen] = useState<boolean>(false);
  const [validReasonForVisit, setValidReasonForVisit] = useState<boolean>(true);
  const [showFields, setShowFields] = useState<AddVisitFormState>(
    isScheduledFollowUp ? 'existingPatientSelected' : 'initialPatientSearch'
  );

  useEffect(() => {
    setReasonForVisit('');
    setReasonForVisitAdditional('');
  }, [serviceCategory]);

  const reasonForVisitOptions = getReasonForVisitOptionsForServiceCategory(serviceCategory ?? '');
  const shouldShowReasonForVisitFields = useMemo(() => {
    return showFields !== 'initialPatientSearch' && reasonForVisitOptions.length > 0;
  }, [showFields, reasonForVisitOptions.length]);
  // general variables
  const navigate = useNavigate();
  const { oystehrZambda } = useApiClients();
  const reasonForVisitErrorMessage = `Input cannot be more than ${MAXIMUM_CHARACTER_LIMIT} characters`;

  const handleAdditionalReasonForVisitChange = (newValue: string): void => {
    setValidReasonForVisit(newValue.length <= MAXIMUM_CHARACTER_LIMIT);
    setReasonForVisitAdditional(newValue);
  };

  useEffect(() => {
    const fetchLocationWithSlotData = async (params: GetScheduleRequestParams, client: Oystehr): Promise<void> => {
      setLoadingSlotState({ status: 'loading', input: undefined });
      try {
        const locationResponse = await getLocations(client, params);
        setLocationWithSlotData(locationResponse);
      } catch (e) {
        console.error('error loading location with slot data', e);
      } finally {
        setLoadingSlotState({ status: 'loaded', input: `${params.slug}` });
      }
    };
    const locationSlug = selectedLocation?.identifier?.find((identifierTemp) => identifierTemp.system === SLUG_SYSTEM)
      ?.value;
    if (!locationSlug) {
      // console.log('show some toast: location is missing slug', selectedLocation, locationSlug);
      return;
    }
    if (
      !oystehrZambda ||
      loadingSlotState.status === 'loading' ||
      (loadingSlotState.status === 'loaded' && loadingSlotState.input === locationSlug)
    ) {
      return;
    }
    void fetchLocationWithSlotData({ slug: locationSlug, scheduleType: ScheduleType.location }, oystehrZambda);
  }, [selectedLocation, loadingSlotState, oystehrZambda]);

  // handle functions
  const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!patientInfo) {
      setErrors({ search: true });
      return;
    }

    const validations: Array<{ invalid: boolean; field: keyof AddVisitErrorState }> = [
      // first name, last name, and phone are empty strings when untouched
      { field: 'firstName', invalid: patientInfo.firstName != null && patientInfo.firstName.length === 0 },
      { field: 'lastName', invalid: patientInfo.lastName != null && patientInfo.lastName.length === 0 },
      { field: 'phone', invalid: patientInfo.phoneNumber != null && patientInfo.phoneNumber.length !== 10 },
      {
        field: 'dateOfBirth',
        invalid: patientInfo.newPatient ? !birthDate : !patientInfo.dateOfBirth,
      },
      { field: 'sexAtBirth', invalid: !patientInfo.sex },
      { field: 'visitType', invalid: !visitType },
      { field: 'serviceCategory', invalid: !serviceCategory },
      { field: 'location', invalid: !!visitType && !selectedLocation },
      { field: 'reasonForVisit', invalid: shouldShowReasonForVisitFields && !reasonForVisit },
    ];
    const fieldErrors = Object.fromEntries(validations.map((v) => [v.field, v.invalid]));
    setErrors((prev) => ({ ...prev, ...fieldErrors }));
    if (validations.some((v) => v.invalid)) {
      return;
    }

    if (
      (visitType === VisitType.InPersonPreBook ||
        visitType === VisitType.InPersonPostTelemed ||
        visitType === VisitType.VirtualScheduled) &&
      slot === undefined
    ) {
      setSelectSlotDialogOpen(true);
      return;
    }
    if (showFields.includes('PatientSearch')) {
      setErrors({ search: true });
      return;
    } else {
      setErrors({ ...errors, search: false });
    }

    if (validDate && validReasonForVisit) {
      setLoading(true);

      console.log('slot', slot);
      if (!oystehrZambda) throw new Error('Zambda client not found');
      let createSlotInput: CreateSlotParams;
      if (visitType === VisitType.InPersonWalkIn || visitType === VisitType.VirtualOnDemand) {
        if (!selectedLocation) {
          enqueueSnackbar('Please select a location', { variant: 'error' });
          setLoading(false);
          return;
        }
        const timezone = getTimezone(selectedLocation?.walkinSchedule ?? selectedLocation);
        createSlotInput = {
          scheduleId: selectedLocation?.walkinSchedule?.id ?? '',
          startISO: DateTime.now().setZone(timezone).toISO() ?? '',
          lengthInMinutes: 15,
          serviceModality: visitType === VisitType.InPersonWalkIn ? ServiceMode['in-person'] : ServiceMode['virtual'],
          walkin: true,
          serviceCategoryCode: serviceCategory,
        };
      } else {
        if (!slot) {
          enqueueSnackbar('Please select a time slot', { variant: 'error' });
          setLoading(false);
          return;
        }
        const scheduleId = slot?.schedule?.reference?.split('/')?.[1] ?? '';
        createSlotInput = {
          scheduleId: scheduleId,
          startISO: slot?.start ?? '',
          lengthInMinutes: getAppointmentDurationFromSlot(slot),
          serviceModality:
            visitType === VisitType.InPersonPreBook || visitType === VisitType.InPersonPostTelemed
              ? ServiceMode['in-person']
              : ServiceMode['virtual'],
          walkin: false,
          postTelemedLabOnly: visitType === VisitType.InPersonPostTelemed,
          serviceCategoryCode: serviceCategory,
        };
      }
      console.log('slot input: ', createSlotInput);
      const persistedSlot = await createSlot(createSlotInput, oystehrZambda);
      const zambdaParams: CreateAppointmentInputParams = {
        patient: {
          ...patientInfo,
          dateOfBirth: patientInfo?.dateOfBirth || birthDate?.toISODate() || undefined,
          reasonForVisit: reasonForVisit,
          reasonAdditional: reasonForVisitAdditional !== '' ? reasonForVisitAdditional : undefined,
        },
        slotId: persistedSlot.id!,
        parentEncounterId,
      };

      let response;
      let apiErr = false;
      try {
        response = await createAppointment(oystehrZambda, zambdaParams);
      } catch (error) {
        console.error(`Failed to add patient: ${error}`);
        enqueueSnackbar('An unexpected error occurred, please try again.', { variant: 'error' });
        apiErr = true;
      } finally {
        setLoading(false);
        if (response && !apiErr) {
          enqueueSnackbar('Visit added successfully', { variant: 'success' });
          navigate('/visits');
        } else {
          setErrors({ submit: true });
        }
      }
    }
  };

  return (
    <PageContainer>
      <Grid container direction="row">
        <Grid item xs={3.5} />
        <Grid item xs={5}>
          <CustomBreadcrumbs
            chain={[
              { link: '/visits', children: 'Tracking Board' },
              { link: '#', children: isScheduledFollowUp ? 'Add Scheduled Follow-up' : 'Add Visit' },
            ]}
          />

          {/* page title */}

          <Typography
            variant="h3"
            marginTop={1}
            color={'primary.dark'}
            data-testid={dataTestIds.addPatientPage.pageTitle}
          >
            {isScheduledFollowUp ? 'Add Scheduled Follow-up Visit' : 'Add Visit'}
          </Typography>

          {/* form content */}
          <Paper>
            <form noValidate onSubmit={(e) => handleFormSubmit(e)}>
              <Stack spacing={2} padding={4}>
                <FormControl fullWidth error={!!errors.visitType}>
                  <InputLabel id="visit-type-label">Visit type *</InputLabel>
                  <Select
                    data-testid={dataTestIds.addPatientPage.visitTypeDropdown}
                    labelId="visit-type-label"
                    id="visit-type-select"
                    value={visitType || ''}
                    label="Visit type *"
                    required
                    onChange={(event) => {
                      setSlot(undefined);
                      setVisitType(event.target.value as VisitType);
                    }}
                  >
                    {BOOKING_CONFIG.ehrBookingOptions.map((option) => (
                      <MenuItem value={option.id} key={option.id}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.visitType && <FormHelperText>Visit type is required</FormHelperText>}
                </FormControl>

                <FormControl fullWidth error={!!errors.serviceCategory}>
                  <InputLabel id="service-category-label">Service category *</InputLabel>
                  <Select
                    data-testid={dataTestIds.addPatientPage.serviceCategoryDropdown}
                    labelId="service-category-label"
                    id="service-category-select"
                    value={serviceCategory || ''}
                    label="Service category *"
                    required
                    disabled={defaultServiceCategory !== ''}
                    onChange={(event) => {
                      setServiceCategory(event.target.value);
                    }}
                  >
                    {BOOKING_CONFIG.serviceCategories.map((sc) => {
                      return (
                        <MenuItem value={sc.category.code} key={sc.category.code}>
                          {sc.category.display}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {errors.serviceCategory && <FormHelperText>Service category is required</FormHelperText>}
                </FormControl>

                <LocationSelect
                  location={selectedLocation}
                  setLocation={setSelectedLocation}
                  updateURL={false}
                  required
                  renderInputProps={{ disabled: !visitType }}
                  locationType={
                    visitType === VisitType.InPersonWalkIn ||
                    visitType === VisitType.InPersonPreBook ||
                    visitType === VisitType.InPersonPostTelemed
                      ? [LocationType.IN_PERSON]
                      : [LocationType.VIRTUAL]
                  }
                  error={!!errors.location}
                  helperText="Location is required"
                />

                {!isScheduledFollowUp && (
                  <AddVisitPatientInformationCard
                    patientInfo={patientInfo}
                    setPatientInfo={setPatientInfo}
                    showFields={showFields}
                    setShowFields={setShowFields}
                    setValidDate={setValidDate}
                    errors={errors}
                    setErrors={setErrors}
                    birthDate={birthDate}
                    setBirthDate={setBirthDate}
                  />
                )}
                {isScheduledFollowUp && patientInfo && (
                  <Typography variant="body1" color="text.secondary">
                    Patient: {patientInfo.firstName} {patientInfo.lastName}
                  </Typography>
                )}

                {/* Visit Information */}
                {shouldShowReasonForVisitFields && (
                  <Box marginTop={4}>
                    <Typography variant="h4" color="primary.dark">
                      Visit information
                    </Typography>
                    <Box marginTop={2}>
                      <FormControl fullWidth error={!!errors.reasonForVisit}>
                        <InputLabel id="reason-for-visit-label">Reason for visit *</InputLabel>
                        <Select
                          data-testid={dataTestIds.addPatientPage.reasonForVisitDropdown}
                          labelId="reason-for-visit-label"
                          id="reason-for-visit-select"
                          value={reasonForVisit || ''}
                          label="Reason for visit *"
                          required
                          onChange={(event) => setReasonForVisit(event.target.value)}
                        >
                          {reasonForVisitOptions.map((reason) => (
                            <MenuItem key={reason.value} value={reason.value}>
                              {reason.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.reasonForVisit && <FormHelperText>Reason for visit is required</FormHelperText>}
                      </FormControl>
                    </Box>
                    <Box marginTop={2}>
                      <FormControl fullWidth>
                        <TextField
                          label="Tell us more (optional)"
                          id="reason-additional-text"
                          value={reasonForVisitAdditional}
                          aria-describedby="reason-additional-helper-text"
                          onChange={(e) => handleAdditionalReasonForVisitChange(e.target.value?.trimStart())}
                          maxRows={2}
                          multiline={true}
                          error={!validReasonForVisit}
                        />
                        {reasonForVisitAdditional.length > 100 && (
                          <Typography
                            variant="caption"
                            color={validReasonForVisit ? 'text.secondary' : 'error'}
                            sx={{ marginTop: 1 }}
                          >
                            {`${reasonForVisitAdditional.length} / ${MAXIMUM_CHARACTER_LIMIT}${
                              !validReasonForVisit ? ` - ${reasonForVisitErrorMessage}` : ''
                            }`}
                          </Typography>
                        )}
                      </FormControl>
                    </Box>
                    {(visitType === VisitType.InPersonPreBook ||
                      visitType === VisitType.InPersonPostTelemed ||
                      visitType === VisitType.VirtualScheduled) && (
                      <SlotPicker
                        slotData={
                          visitType === VisitType.InPersonPostTelemed
                            ? locationWithSlotData?.telemedAvailable?.map((si) => si.slot)
                            : locationWithSlotData?.available?.map((si) => si.slot)
                        }
                        slotsLoading={loadingSlotState.status === 'loading'}
                        selectedLocation={selectedLocation}
                        timezone={locationWithSlotData?.location?.timezone || 'Undefined'}
                        selectedSlot={slot}
                        setSelectedSlot={setSlot}
                      />
                    )}
                  </Box>
                )}

                {/* form buttons */}
                <Box marginTop={4}>
                  {errors.submit && (
                    <Typography color="error" variant="body2" mb={2}>
                      Failed to add patient. Please try again.
                    </Typography>
                  )}
                  {errors.search && (
                    <Typography color="error" variant="body2" mb={2}>
                      Please search for patients before adding
                    </Typography>
                  )}
                  <LoadingButton
                    data-testid={dataTestIds.addPatientPage.addButton}
                    variant="contained"
                    type="submit"
                    loading={loading}
                    sx={{
                      borderRadius: 100,
                      textTransform: 'none',
                      fontWeight: 600,
                      marginRight: 1,
                    }}
                  >
                    Add {visitType}
                  </LoadingButton>
                  <Button
                    data-testid={dataTestIds.addPatientPage.cancelButton}
                    sx={{
                      borderRadius: 100,
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                    onClick={() => {
                      navigate('/visits');
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Stack>
            </form>
            <CustomDialog
              open={selectSlotDialogOpen}
              title="Please select a date and time"
              description="To continue, please select an available appointment."
              closeButtonText="Close"
              handleClose={() => setSelectSlotDialogOpen(false)}
            />
          </Paper>
        </Grid>

        <Grid item xs={3.5} />
      </Grid>
    </PageContainer>
  );
}
